import { IconSelectClean } from "@components/inputs/select/IconSelect";
import { ifAll, ifAny, IGetValueArgs, not, TGetValueFn } from "@components/smart/FieldInfo";
import { IDependentFieldDef, IFormGroupDef } from "@components/smart/smartFormGroup/SmartFormGroup";
import { getDefaultSystemVariant } from "@components/variantSelector/VariantOdata";
import { createPath } from "@odata/BindingContext";
import {
    DocumentBankAccountEntity,
    DocumentBusinessPartnerEntity,
    DocumentEntity,
    DocumentItemEntity,
    EntitySetName,
    IDocumentBusinessPartnerEntity,
    IOtherLiabilityEntity,
    IPrEmployeeEntity,
    OtherLiabilityEntity,
    PrEmployeeBankAccountEntity,
    PrEmployeeEntity,
    PrEmployeePermanentAddressEntity
} from "@odata/GeneratedEntityTypes";
import { DocumentBusinessPartnerTypeCode, DocumentTypeCode, SelectionCode } from "@odata/GeneratedEnums";
import { ITEMS_VAT_DEDUCTION_PATH, SAVED_VATS_PATH } from "@pages/admin/vatRules/VatRules.global.utils";
import { getBusinessPartnerBankAccountAdditionalProperties } from "@pages/businessPartner/BusinessPartner.utils";
import { IDocumentExtendedEntity } from "@pages/documents/DocumentInterfaces";
import { IDefinition } from "@pages/PageUtils";
import { getEmployeeFilterDef, getEmployeeSelectDef } from "@pages/payroll/employee/Employee.utils";
import { hasPayrollEnabled } from "@utils/CompanyUtils";
import { joinWith, universalPersonNameFormatter } from "@utils/string";
import i18next from "i18next";

import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { GroupedField, NavigationSource, QueryParam, ToolbarItemType } from "../../../enums";
import { TableStorage } from "../../../model/TableStorage";
import { getQueryParameters } from "../../../routes/Routes.utils";
import { FormStorage } from "../../../views/formView/FormStorage";
import { ICustomButtonDefArgs, TableActionOrder, TTableToolbarItem } from "../../../views/table/TableToolbar.utils";

export const ADD_SELECT_BUTTON = "AddSelectButton";

export function getToolbarAddButtonConfig(type: "OtherLiability" | "OtherReceivable", storage: TableStorage, ref: React.RefObject<IconSelectClean>, args?: ICustomButtonDefArgs): TTableToolbarItem {
    return {
        id: ADD_SELECT_BUTTON,
        itemType: ToolbarItemType.IconSelect,
        iconName: "Add",
        label: i18next.t(`Document:Table.NewDocument`),
        order: TableActionOrder.Add,
        itemProps: {
            isTransparent: true,
            selectProps: {
                isActive: storage.data.addingRow || args?.isActive,
                isDisabled: !!storage.tableAction || args?.isDisabled,
                ref
            },
            items: [{
                id: DocumentBusinessPartnerTypeCode.BusinessPartner,
                label: i18next.t(`Document:Table.New${type}`),
                iconName: `${type}`
            }, {
                id: DocumentBusinessPartnerTypeCode.Employee,
                label: i18next.t(`Document:Table.New${type}Employee`),
                iconName: `${type}Employee`
            }]
        }
    };
}

export function isEmployeeBPType(bp: IDocumentBusinessPartnerEntity): boolean {
    return bp?.DocumentBusinessPartnerTypeCode === DocumentBusinessPartnerTypeCode.Employee;
}

export function newDocIsEmployeeType(): boolean {
    return getQueryParameters()[QueryParam.Type] === DocumentBusinessPartnerTypeCode.Employee;
}

export function addBusinessPartnerType(definition: IDefinition): void {
    const { fieldDefinition, additionalProperties } = definition.form;
    const docTypePath = createPath(DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.DocumentBusinessPartnerType);

    fieldDefinition[docTypePath] = {
        isVisible: false,
        clearIfInvisible: false,
        defaultValue: (args: IGetValueArgs) => {
            const isEmployeeType = newDocIsEmployeeType();
            return isEmployeeType ? DocumentBusinessPartnerTypeCode.Employee : DocumentBusinessPartnerTypeCode.BusinessPartner;
        },
        customizationData: {
            useForCustomization: false
        }
    };

    additionalProperties.push({ id: docTypePath });
}

const isEmployeeBusinessPartnerType: TGetValueFn<boolean> = ({ storage }) => {
    const bpBc = storage.data.bindingContext.navigate(DocumentEntity.BusinessPartner);
    const type = storage.getValue(bpBc.navigate(DocumentBusinessPartnerEntity.DocumentBusinessPartnerTypeCode));
    return type === DocumentBusinessPartnerEntity.Employee;
};

export function enhanceWithEmployeeBusinessPartnerDefinition(definition: IDefinition, bpGroup: IFormGroupDef, context: IAppContext): void {
    if (!hasPayrollEnabled(context.getCompany())) {
        return;
    }

    const prefix = DocumentEntity.BusinessPartner;
    const employeePath = `${prefix}/${DocumentBusinessPartnerEntity.Employee}`;

    definition.form.fieldDefinition[employeePath] = {
        ...getEmployeeSelectDef(),
        groupedField: GroupedField.None,
        isVisible: isEmployeeBusinessPartnerType,
        isRequired: isEmployeeBusinessPartnerType,
        customizationData: {
            useForCustomization: false
        }
    };
    bpGroup.rows[0].forEach(item => {
        definition.form.fieldDefinition[item.id].isVisible = not(isEmployeeBusinessPartnerType);
        definition.form.fieldDefinition[item.id].clearIfInvisible = false;
    });
    // put the employee field between other BP fields, so it's grouped together with them in variant config
    bpGroup.rows[0].splice(1, 0, { id: employeePath });

    // enhance local dependent fields
    const localDependentFields: IDependentFieldDef[] = [
        {
            from: { id: PrEmployeeEntity.FirstName },
            to: { id: DocumentBusinessPartnerEntity.FirstName },
            navigateFrom: NavigationSource.Parent
        },
        {
            from: { id: PrEmployeeEntity.LastName },
            to: { id: DocumentBusinessPartnerEntity.LastName },
            navigateFrom: NavigationSource.Parent
        },
        {
            from: { id: PrEmployeeEntity.Email },
            to: { id: DocumentBusinessPartnerEntity.Email },
            navigateFrom: NavigationSource.Parent
        },
        {
            from: { id: PrEmployeeEntity.PhoneNumber },
            to: { id: DocumentBusinessPartnerEntity.PhoneNumber },
            navigateFrom: NavigationSource.Parent
        },
        {
            from: { id: createPath(PrEmployeeEntity.PermanentAddress, PrEmployeePermanentAddressEntity.Street) },
            to: { id: DocumentBusinessPartnerEntity.Street },
            navigateFrom: NavigationSource.Parent,
            formatter: (val, args) => {
                const { item } = args;
                const { Street, HouseNumber, StreetNumber } = (item as IPrEmployeeEntity).PermanentAddress || {};
                return joinWith(" ", Street, joinWith("/", HouseNumber, StreetNumber));
            }
        },
        {
            from: { id: createPath(PrEmployeeEntity.PermanentAddress, PrEmployeePermanentAddressEntity.City) },
            to: { id: DocumentBusinessPartnerEntity.City },
            navigateFrom: NavigationSource.Parent
        },
        {
            from: { id: createPath(PrEmployeeEntity.PermanentAddress, PrEmployeePermanentAddressEntity.PostalCode) },
            to: { id: DocumentBusinessPartnerEntity.PostalCode },
            navigateFrom: NavigationSource.Parent
        },
        {
            from: { id: createPath(PrEmployeeEntity.PermanentAddress, PrEmployeePermanentAddressEntity.CountryCode) },
            to: { id: DocumentBusinessPartnerEntity.CountryCode },
            navigateFrom: NavigationSource.Parent
        },
        {
            from: { id: createPath(PrEmployeeEntity.BankAccount, PrEmployeeBankAccountEntity.AccountNumber) },
            to: { id: createPath(DocumentEntity.BankAccount, DocumentBankAccountEntity.AccountNumber) },
            navigateFrom: NavigationSource.Root
        },
        {
            from: { id: createPath(PrEmployeeEntity.BankAccount, PrEmployeeBankAccountEntity.AbaNumber) },
            to: { id: createPath(DocumentEntity.BankAccount, DocumentBankAccountEntity.AbaNumber) },
            navigateFrom: NavigationSource.Root
        },
        {
            from: { id: createPath(PrEmployeeEntity.BankAccount, PrEmployeeBankAccountEntity.Bank) },
            to: { id: createPath(DocumentEntity.BankAccount, DocumentBankAccountEntity.Bank) },
            navigateFrom: NavigationSource.Root
        },
        {
            from: { id: createPath(PrEmployeeEntity.BankAccount, PrEmployeeBankAccountEntity.IBAN) },
            to: { id: createPath(DocumentEntity.BankAccount, DocumentBankAccountEntity.IBAN) },
            navigateFrom: NavigationSource.Root
        },
        {
            from: { id: createPath(PrEmployeeEntity.BankAccount, PrEmployeeBankAccountEntity.SWIFT) },
            to: { id: createPath(DocumentEntity.BankAccount, DocumentBankAccountEntity.SWIFT) },
            navigateFrom: NavigationSource.Root
        },
        {
            from: { id: PrEmployeeEntity.SymbolVariable },
            to: { id: DocumentEntity.SymbolVariable },
            navigateFrom: NavigationSource.Root
        },
        {
            from: { id: PrEmployeeEntity.SymbolSpecific },
            to: { id: DocumentEntity.SymbolSpecific },
            navigateFrom: NavigationSource.Root
        },
        {
            from: { id: PrEmployeeEntity.SymbolConstant },
            to: { id: DocumentEntity.SymbolConstant },
            navigateFrom: NavigationSource.Root
        },
        {
            from: { id: PrEmployeeEntity.RemittanceInformation },
            to: { id: DocumentEntity.RemittanceInformation },
            navigateFrom: NavigationSource.Root
        },
    ];
    getBusinessPartnerBankAccountAdditionalProperties(PrEmployeeEntity.BankAccount, true)
        .forEach(field => localDependentFields.push({
            from: { id: field.id },
            to: { id: createPath(DocumentBusinessPartnerEntity.Employee, field.id) },
            navigateFrom: NavigationSource.Parent
        }));
    definition.form.fieldDefinition[employeePath].fieldSettings.localDependentFields.push(...localDependentFields);
    // these fields are already present in the additionalProps, exclude them...
    const excludedAdditionalFields: string[] = [PrEmployeeEntity.FirstName, PrEmployeeEntity.LastName];
    const additionalProperties = localDependentFields
        .map(field => excludedAdditionalFields.includes(field.from.id) ? null : { id: field.from.id })
        .filter(Boolean);

    additionalProperties.push(
        { id: createPath(PrEmployeeEntity.PermanentAddress, PrEmployeePermanentAddressEntity.StreetNumber) },
        { id: createPath(PrEmployeeEntity.PermanentAddress, PrEmployeePermanentAddressEntity.HouseNumber) }
    );

    definition.form.fieldDefinition[employeePath].additionalProperties.push(...additionalProperties);
    definition.form.fieldDefinition[employeePath].columns[0].additionalProperties.push(...additionalProperties);

    // invisible fields
    const employeeHiddenFields = [
        createPath(prefix, DocumentBusinessPartnerEntity.VatStatus),
        createPath(prefix, DocumentBusinessPartnerEntity.FirstName),
        createPath(prefix, DocumentBusinessPartnerEntity.LastName),
        OtherLiabilityEntity.DateTaxableSupply,
        OtherLiabilityEntity.DateVatDeduction,
        SAVED_VATS_PATH,
        createPath(OtherLiabilityEntity.Items, DocumentItemEntity.Vat),
        createPath(OtherLiabilityEntity.Items, DocumentItemEntity.TransactionAmountNet),
        createPath(OtherLiabilityEntity.Items, DocumentItemEntity.TransactionAmountVat),
        createPath(OtherLiabilityEntity.Items, ITEMS_VAT_DEDUCTION_PATH),
    ];
    employeeHiddenFields.forEach(fieldId => {
        const def = definition.form.fieldDefinition[fieldId];
        if (def) {
            def.isVisible = def.isVisible ? ifAll(def.isVisible, not(isEmployeeBusinessPartnerType)) : not(isEmployeeBusinessPartnerType);
            def.clearIfInvisible = false;
        }
    });

    // all expanded fields are read-only
    const collapsedRows = definition.form.fieldDefinition[`${prefix}/${DocumentBusinessPartnerEntity.TaxNumber}`].collapsedRows;
    collapsedRows?.forEach(row => {
        row.forEach(item => {
            const def = definition.form.fieldDefinition[item.id];
            if (def) {
                def.isReadOnly = def.isReadOnly ? ifAny(def.isReadOnly, isEmployeeBusinessPartnerType) : isEmployeeBusinessPartnerType;
            }
        });
    });
}

export function enhanceEmployeeBPTableDefinition(definition: IDefinition, context: IAppContext, docType: DocumentTypeCode, filterIndex = 0): void {
    const transFile = docType === DocumentTypeCode.OtherLiability ? "OtherLiabilities" : "OtherReceivables";
    const labelStr = i18next.t(`${transFile}:FormGroup.BusinessPartner`).toString();
    const hasPayroll = hasPayrollEnabled(context.getCompany());
    // Add Employee filter on table view
    const { filterDefinition } = definition.table.filterBarDef[filterIndex];
    filterDefinition["BusinessPartner"].label = labelStr;

    if (hasPayroll) {
        const filterDef = getEmployeeFilterDef(createPath(DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.Employee));
        Object.keys(filterDef).forEach(key => filterDefinition[key] = filterDef[key]);
    }

    // Business partner column - show also Employee, disable sorting
    const { BusinessPartner } = definition.table.columnDefinition;
    BusinessPartner.label = labelStr;

    if (hasPayroll) {
        BusinessPartner.additionalProperties.push(
            { id: DocumentBusinessPartnerEntity.DocumentBusinessPartnerTypeCode },
            { id: DocumentBusinessPartnerEntity.FirstName },
            { id: DocumentBusinessPartnerEntity.LastName },
            { id: createPath(DocumentBusinessPartnerEntity.Employee, PrEmployeeEntity.Id) }
        );
        BusinessPartner.disableSort = true;
    }
}

export async function checkEmployeeTypeAndSetVariant(storage: FormStorage): Promise<boolean> {
    const { bindingContext } = storage.data;
    let isEmployee: boolean;
    if (bindingContext.isNew()) {
        isEmployee = newDocIsEmployeeType();
    } else {
        try {
            // check from data is current doc is Employee type
            const entitySetName = bindingContext.getEntitySet().getName() as EntitySetName;
            const query = storage.oData.getEntitySetWrapper(entitySetName)
                .query(bindingContext.getKey())
                .select(DocumentEntity.Id)
                .expand(DocumentEntity.BusinessPartner, q =>
                    q.select(DocumentBusinessPartnerEntity.DocumentBusinessPartnerTypeCode));

            const result = await query.fetchData<IDocumentExtendedEntity>();
            isEmployee = isEmployeeBPType(result.value.BusinessPartner);
        } catch(e) {
            // fall silently
            isEmployee = false;
        }
    }
    // set default variant if not set for Employee type
    if (isEmployee) {
        const { allVariants, currentVariant } = storage.data.variants;
        const systemVariant = getDefaultSystemVariant(Object.values(allVariants));
        if (storage.getLocalStorageVariant() || systemVariant.id !== currentVariant.id) {
            // switch to system variant
            await storage.changeVariant(systemVariant);
            return true;
        }
    }
    return false;
}

export function updateEmployeeRelatedDataBeforeSave(preparedData: IOtherLiabilityEntity, storage: FormStorage): void {
    if (preparedData.BusinessPartner?.Employee?.Id) {
        // For Employee BP, we want to keep the Name field in sync with the Employee name.
        preparedData.BusinessPartner.Name = universalPersonNameFormatter(preparedData.BusinessPartner);
    }

    if (isEmployeeBPType(preparedData.BusinessPartner)) {
        // For Employee BP, VatClassificationSelection is not applicable, set it to None to avoid any issues with validation or backend processing.
        preparedData.VatClassificationSelection = {
            SelectionCode: SelectionCode.None
        };

        // keep invisible dates in sync with issuedDate
        preparedData.DateTaxableSupply = preparedData.DateIssued;
        if (storage.data.bindingContext.isValidNavigation(OtherLiabilityEntity.DateVatDeduction)) {
            preparedData.DateVatDeduction = preparedData.DateIssued;
        }
    }
}